import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Calendar, Clock, CheckCircle2, AlertCircle, Trash2, Edit, Home, LogOut, Database, Zap, Book } from 'lucide-react';
import { motion } from "framer-motion";
import { Card, CardHeader, CardTitle, CardContent } from '../client/components/ui/card';
import { Button } from '../client/components/ui/button';
import { useAuth } from '../client/lib/useAuth';
import { ProjectService, Project } from '../client/lib/projects';
import { FileUploadModal } from '../client/components/FileUploadModal';
import { DeleteConfirmationModal } from '../client/components/DeleteConfirmationModal';
import BillingStatus from '../client/components/BillingStatus';

const AIBookIcon = () => (
  <div className="relative">
    <div className="absolute inset-0 rounded-lg bg-cyber-blue opacity-20 blur-md"></div>
    <svg viewBox="0 0 120 120" className="w-12 h-12 relative z-10">
      <defs>
        <linearGradient id="headerLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--cyber-blue))" />
          <stop offset="50%" stopColor="hsl(var(--cyber-purple))" />
          <stop offset="100%" stopColor="hsl(var(--cyber-green))" />
        </linearGradient>
        <linearGradient id="headerBookGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--cyber-blue))" />
          <stop offset="100%" stopColor="hsl(var(--cyber-purple))" />
        </linearGradient>
      </defs>
      
      {/* Book base */}
      <rect x="20" y="30" width="80" height="60" rx="4" fill="url(#headerBookGradient)" className="drop-shadow-lg" />
      <rect x="22" y="32" width="76" height="56" rx="2" fill="rgba(255,255,255,0.1)" />
      
      {/* Book pages/lines */}
      <line x1="30" y1="42" x2="70" y2="42" stroke="white" strokeWidth="1.5" opacity="0.8" />
      <line x1="30" y1="50" x2="85" y2="50" stroke="white" strokeWidth="1.5" opacity="0.8" />
      <line x1="30" y1="58" x2="75" y2="58" stroke="white" strokeWidth="1.5" opacity="0.8" />
      <line x1="30" y1="66" x2="80" y2="66" stroke="white" strokeWidth="1.5" opacity="0.8" />
      <line x1="30" y1="74" x2="65" y2="74" stroke="white" strokeWidth="1.5" opacity="0.8" />
      
      {/* AI Brain overlay */}
      <circle cx="85" cy="45" r="22" fill="url(#headerLogoGradient)" className="drop-shadow-lg" opacity="0.9" />
      <circle cx="85" cy="45" r="20" fill="rgba(255,255,255,0.1)" />
      
      {/* Neural network pattern */}
      <circle cx="78" cy="38" r="2" fill="white" opacity="0.9" />
      <circle cx="92" cy="40" r="2" fill="white" opacity="0.9" />
      <circle cx="80" cy="52" r="2" fill="white" opacity="0.9" />
      <circle cx="90" cy="50" r="2" fill="white" opacity="0.9" />
      <circle cx="85" cy="45" r="2" fill="white" />
      
      {/* Connecting lines for neural network */}
      <line x1="78" y1="38" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
      <line x1="92" y1="40" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
      <line x1="80" y1="52" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
      <line x1="90" y1="50" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
      
      {/* Index symbol */}
      <text x="35" y="48" fill="white" fontSize="8" fontWeight="bold" opacity="0.7">A</text>
      <text x="40" y="56" fill="white" fontSize="6" opacity="0.7">B</text>
      <text x="45" y="64" fill="white" fontSize="6" opacity="0.7">C</text>
    </svg>
  </div>
);

const ProjectsList: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated, logout } = useAuth();
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

    const projectService = ProjectService.getInstance();

    const fetchProjects = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const result = await projectService.getProjects();
            if (result.success && result.projects) {
                setProjects(result.projects);
            } else {
                throw new Error(result.error || 'Failed to fetch projects');
            }
        } catch (err: any) {
            console.error('Error fetching projects:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/');
            return;
        }

        fetchProjects();
    }, [isAuthenticated, navigate]);

    const handleDeleteProject = (project: Project) => {
        setProjectToDelete(project);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteProject = async () => {
        if (!projectToDelete) return;

        setDeletingProjectId(projectToDelete.id);
        
        try {
            const result = await projectService.deleteProject(projectToDelete.id);
            if (result.success) {
                setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
            } else {
                throw new Error(result.error || 'Failed to delete index');
            }
        } catch (err: any) {
            console.error('Error deleting project:', err);
            setError(err.message);
        } finally {
            setDeletingProjectId(null);
            setProjectToDelete(null);
        }
    };

    const handleViewProject = (projectId: string) => {
        navigate(`/projects/${projectId}`);
    };

    const handleGoHome = () => {
        navigate('/');
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Convert technical status to user-friendly descriptions
    const getDisplayStatus = (project: Project): string => {
        if (project.reviewStage) {
            switch (project.reviewStage) {
                case 'B':
                    return 'Review Terms';
                case 'C':
                case 'C1':
                case 'C2':
                    return 'Analyze Term Relationships';
                case 'C3':
                case 'D':
                    return 'Review Final Index and Subentries';
                default:
                    return 'Review Terms';
            }
        }
        
        switch (project.status) {
            case 'pending':
            case 'processing':
                return 'Starting Index';
            case 'completed':
                return 'Completed';
            case 'error':
                return 'Error';
            default:
                return 'Processing';
        }
    };

    const renderStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircle2 className="h-5 w-5 text-cyber-green" />;
            case 'error':
                return <AlertCircle className="h-5 w-5 text-destructive" />;
            case 'processing':
                return <Clock className="h-5 w-5 text-cyber-blue" />;
            default:
                return <Clock className="h-5 w-5 text-muted-foreground" />;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'bg-cyber-green text-background';
            case 'error':
                return 'bg-destructive text-background';
            case 'processing':
                return 'bg-cyber-orange text-background';
                default:
                return 'bg-muted text-background';
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center space-y-4 flex flex-col items-center">
                    <div className="relative scale-150 flex justify-center">
                        <AIBookIcon />
                    </div>
                    <h2 className="text-2xl font-bold text-foreground">Loading Indexes</h2>
                    <p className="text-muted-foreground">Fetching your recent work...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">

                {/* Header */}
            <div className="glass-card border-b border-border/50 sticky top-0 z-50 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <Button 
                            variant="ghost"
                            className="text-muted-foreground hover:text-cyber-blue transition-colors"
                        >
                            <AIBookIcon />
                            <span className="ml-2">My Indexes</span>
                        </Button>
                        
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                onClick={handleGoHome}
                                className="text-muted-foreground hover:text-cyber-blue transition-colors"
                            >
                                <Home className="w-4 h-4 mr-2" />
                                Home
                            </Button>
                            
                            <Button
                                onClick={() => setIsUploadModalOpen(true)}
                                className="btn-cyber px-6 py-2 font-semibold tracking-wide"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Index New Book
                        </Button>
                            
                        <Button 
                                variant="ghost"
                            onClick={handleLogout} 
                                className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                                <LogOut className="w-4 h-4" />
                        </Button>
                        </div>
                    </div>
                    </div>
                </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-8 relative z-10">
                {/* Billing Status - Minimal */}
                <div className="mb-4">
                    <BillingStatus className="max-w-sm opacity-60 hover:opacity-100 transition-opacity" />
                </div>

                {/* Subtle File Size Notice */}
                <div className="mb-6 max-w-sm">
                    <p className="text-xs text-muted-foreground/70 flex items-center gap-2">
                        <FileText className="w-3 h-3" />
                        For optimal performance, please upload PDFs under 10MB
                    </p>
                </div>

                {error && (
                    <div className="mb-6 flex items-center gap-3 p-4 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive animate-slide-in">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="font-medium">{error}</span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={fetchProjects}
                            className="ml-auto text-destructive hover:text-destructive"
                        >
                            Retry
                        </Button>
                            </div>
                )}

                {projects.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="relative mb-8 scale-[2] pt-8 pb-8">
                            <AIBookIcon />
                        </div>
                        <h2 className="text-3xl font-bold text-foreground mb-4">No indexes yet</h2>
                        <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
                            Start your first book index by uploading a document.
                            </p>
                        <Button
                            onClick={() => setIsUploadModalOpen(true)}
                            className="btn-cyber px-8 py-4 text-lg font-semibold tracking-wide"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Index Your First Book
                            </Button>
                    </div>
                ) : (
                    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {projects.map((project) => (
                            <div
                                key={project.id}
                                className="relative group"
                            >
                                {/* Enhanced Card with better depth and prominence */}
                                <Card 
                                    className="relative overflow-hidden cursor-pointer transition-all duration-700 
                                             bg-card/80 backdrop-blur-md border-2 border-border/50 
                                             hover:border-cyber-blue/60 hover:shadow-2xl hover:shadow-cyber-blue/20
                                             hover:scale-[1.02] hover:-translate-y-3 
                                             group-hover:bg-card/90"
                                    onClick={() => handleViewProject(project.id)}
                                >
                                    {/* Gradient overlay for depth */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-cyber-blue/5 via-transparent to-cyber-purple/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                                    
                                    {/* Animated glow border */}
                                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-cyber-blue/20 via-cyber-purple/20 to-cyber-green/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10 blur-xl" />
                                    
                                    <CardHeader className="relative pb-4 space-y-4">
                                        {/* Project Icon and Actions Row */}
                                    <div className="flex items-start justify-between">
                                            <div className="relative">
                                                <div className="absolute inset-0 bg-cyber-blue/30 rounded-2xl blur-lg opacity-50 group-hover:opacity-100 transition-all duration-500"></div>
                                                <div className="relative bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 p-4 rounded-2xl transition-all duration-500">
                                                    <div className="w-8 h-8 group-hover:scale-110 transition-transform duration-300" style={{filter: 'drop-shadow(0 0 2px white)'}}>
                                                        <svg viewBox="0 0 120 120" className="w-full h-full">
                                                            <defs>
                                                                <linearGradient id={`cardLogoGradient-${project.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                                                    <stop offset="0%" stopColor="hsl(var(--cyber-blue))" />
                                                                    <stop offset="50%" stopColor="hsl(var(--cyber-purple))" />
                                                                    <stop offset="100%" stopColor="hsl(var(--cyber-green))" />
                                                                </linearGradient>
                                                                <linearGradient id={`cardBookGradient-${project.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                                                    <stop offset="0%" stopColor="hsl(var(--cyber-blue))" />
                                                                    <stop offset="100%" stopColor="hsl(var(--cyber-purple))" />
                                                                </linearGradient>
                                                            </defs>
                                                            
                                                            {/* Book base */}
                                                            <rect x="20" y="30" width="80" height="60" rx="4" fill={`url(#cardBookGradient-${project.id})`} className="drop-shadow-lg" />
                                                            <rect x="22" y="32" width="76" height="56" rx="2" fill="rgba(255,255,255,0.1)" />
                                                            
                                                            {/* Book pages/lines */}
                                                            <line x1="30" y1="42" x2="70" y2="42" stroke="white" strokeWidth="1.5" opacity="0.8" />
                                                            <line x1="30" y1="50" x2="85" y2="50" stroke="white" strokeWidth="1.5" opacity="0.8" />
                                                            <line x1="30" y1="58" x2="75" y2="58" stroke="white" strokeWidth="1.5" opacity="0.8" />
                                                            <line x1="30" y1="66" x2="80" y2="66" stroke="white" strokeWidth="1.5" opacity="0.8" />
                                                            <line x1="30" y1="74" x2="65" y2="74" stroke="white" strokeWidth="1.5" opacity="0.8" />
                                                            
                                                            {/* AI Brain overlay */}
                                                            <circle cx="85" cy="45" r="22" fill={`url(#cardLogoGradient-${project.id})`} className="drop-shadow-lg" opacity="0.9" />
                                                            <circle cx="85" cy="45" r="20" fill="rgba(255,255,255,0.1)" />
                                                            
                                                            {/* Neural network pattern */}
                                                            <circle cx="78" cy="38" r="2" fill="white" opacity="0.9" />
                                                            <circle cx="92" cy="40" r="2" fill="white" opacity="0.9" />
                                                            <circle cx="80" cy="52" r="2" fill="white" opacity="0.9" />
                                                            <circle cx="90" cy="50" r="2" fill="white" opacity="0.9" />
                                                            <circle cx="85" cy="45" r="2" fill="white" />
                                                            
                                                            {/* Connecting lines for neural network */}
                                                            <line x1="78" y1="38" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
                                                            <line x1="92" y1="40" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
                                                            <line x1="80" y1="52" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
                                                            <line x1="90" y1="50" x2="85" y2="45" stroke="white" strokeWidth="1" opacity="0.6" />
                                                            
                                                            {/* Index symbol */}
                                                            <text x="35" y="48" fill="white" fontSize="8" fontWeight="bold" opacity="0.7">A</text>
                                                            <text x="40" y="56" fill="white" fontSize="6" opacity="0.7">B</text>
                                                            <text x="45" y="64" fill="white" fontSize="6" opacity="0.7">C</text>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Action buttons */}
                                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewProject(project.id);
                                                    }}
                                                    className="h-9 w-9 p-0 text-muted-foreground hover:text-cyber-blue hover:bg-cyber-blue/10 transition-all duration-300 rounded-xl"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteProject(project);
                                                    }}
                                                    disabled={deletingProjectId === project.id}
                                                    className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-300 rounded-xl"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Project Title */}
                                        <div>
                                            <CardTitle className="text-2xl font-bold text-muted-foreground leading-tight">
                                                {project.name}
                                            </CardTitle>
                                        </div>

                                        {/* Status and Date Row */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                            {renderStatusIcon(project.status)}
                                                <span className="text-sm text-muted-foreground italic">
                                                    {getDisplayStatus(project)}
                                            </span>
                                        </div>
                                        </div>
                                    </CardHeader>
                                    
                                    <CardContent className="relative pt-0">
                                        <div className="space-y-4">
                                            {/* Creation Date */}
                                            <div className="flex items-center gap-3 text-muted-foreground">
                                                <Calendar className="w-4 h-4" />
                                                <span className="text-sm font-medium">Created {formatDate(project.createdAt)}</span>
                                            </div>

                                            {/* Description */}
                                            {project.description && (
                                                <div className="pt-2">
                                                    <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                                                        {project.description}
                                                    </p>
                                        </div>
                                            )}
                                    </div>
                                </CardContent>
                            </Card>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <FileUploadModal 
                open={isUploadModalOpen} 
                onOpenChange={setIsUploadModalOpen} 
            />

            <DeleteConfirmationModal
                open={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
                onConfirm={confirmDeleteProject}
                title="Delete Index"
                message={`Are you sure you want to delete "${projectToDelete?.name}"? This action cannot be undone.`}
                isDeleting={deletingProjectId === projectToDelete?.id}
            />
        </div>
    );
};

export default ProjectsList; 